import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import TicketModal from '../user-dashboard/components/TicketModal';
import SMSSendModal from './components/SMSSendModal';
import RealtimeTest from '../../components/RealtimeTest';
import { useAuth } from '../../contexts/AuthContext';
import { useUserBookings } from '../../hooks/useUserBookings';

const MyTickets = () => {
  const [filter, setFilter] = useState('all'); // all, upcoming, past
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsBooking, setSmsBooking] = useState(null);
  const [recentlyUpdated, setRecentlyUpdated] = useState(new Set());
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const { userProfile, loading: authLoading } = useAuth();
  
  const handleBookingUpdate = (bookingId, eventType) => {
    setRecentlyUpdated(prev => new Set([...prev, bookingId]));
  };

  const { 
    bookings, 
    loading, 
    error, 
    isRealtimeConnected,
    getUpcomingBookings, 
    getPastBookings 
  } = useUserBookings(handleBookingUpdate);

  // Track recently updated bookings for visual feedback
  useEffect(() => {
    if (bookings?.length > 0) {
      // Clear the recently updated set after 3 seconds
      const timer = setTimeout(() => {
        setRecentlyUpdated(new Set());
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [bookings]);

  // Auto-hide notifications
  useEffect(() => {
    if (notification?.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification?.show]);

  const filteredBookings = (() => {
    switch (filter) {
      case 'upcoming':
        return getUpcomingBookings();
      case 'past':
        return getPastBookings();
      default:
        return bookings;
    }
  })();

  const handleViewTicket = (booking) => {
    setSelectedBooking(booking);
    setShowTicketModal(true);
  };

  const handleSendSMS = (booking) => {
    setSmsBooking(booking);
    setShowSMSModal(true);
  };

  const handleSMSSuccess = (message) => {
    setNotification({ show: true, message, type: 'success' });
  };

  const handleSMSError = (message) => {
    setNotification({ show: true, message, type: 'error' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'attended':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeUntilEvent = (eventDate) => {
    if (!eventDate) return '';
    
    const now = new Date();
    const event = new Date(eventDate);
    const isToday = event?.toDateString() === now?.toDateString();
    const isTomorrow = event?.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000)?.toDateString();
    
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    
    const diffTime = event - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Past event';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `${diffDays} days`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
    return `${Math.ceil(diffDays / 30)} months`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading your tickets...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertCircle" size={32} className="mx-auto mb-4 text-destructive" />
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => window.location?.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Notification */}
      {notification?.show && (
        <div className={`fixed top-20 right-4 z-50 max-w-sm rounded-lg border p-4 shadow-lg ${
          notification?.type === 'success' ?'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-start">
            <Icon 
              name={notification?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={20} 
              className="mr-3 mt-0.5" 
            />
            <div className="flex-1">
              <p className="text-sm font-medium">{notification?.message}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setNotification({ show: false, message: '', type: '' })}
              className="ml-2 h-6 w-6 p-0"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="pt-16 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-foreground">My Tickets</h1>
                {isRealtimeConnected && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Live
                  </div>
                )}
              </div>
              <p className="text-muted-foreground mt-1">
                Manage and view all your event tickets
                {isRealtimeConnected && (
                  <span className="text-green-600 ml-2">• Updates in real-time</span>
                )}
              </p>
            </div>
            
            <Button variant="default" asChild>
              <Link to="/event-discovery">
                <Icon name="Plus" size={16} className="mr-2" />
                Book New Event
              </Link>
            </Button>
          </div>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {[
              { id: 'all', label: 'All Tickets', count: bookings?.length },
              { id: 'upcoming', label: 'Upcoming', count: bookings?.filter(b => {
                const eventDate = b?.events?.event_date ? new Date(b.events.event_date) : null;
                return eventDate && eventDate > new Date();
              })?.length },
              { id: 'past', label: 'Past Events', count: bookings?.filter(b => {
                const eventDate = b?.events?.event_date ? new Date(b.events.event_date) : null;
                return eventDate && eventDate <= new Date();
              })?.length }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setFilter(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  filter === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <span>{tab?.label}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  filter === tab?.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {tab?.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredBookings?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Ticket" size={64} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium text-foreground mb-2">
              {filter === 'all' ? 'No tickets found' : 
               filter === 'upcoming' ? 'No upcoming events' : 'No past events'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' ? 'You haven\'t booked any events yet.' :
               filter === 'upcoming' ? 'You don\'t have any upcoming events.' :
               'You haven\'t attended any events yet.'}
            </p>
            <Button variant="default" asChild>
              <Link to="/event-discovery">
                <Icon name="Search" size={16} className="mr-2" />
                Discover Events
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBookings?.map((booking) => {
              const eventDate = booking?.events?.event_date ? new Date(booking.events.event_date) : null;
              const isUpcoming = eventDate && eventDate > new Date();
              
              const isRecentlyUpdated = recentlyUpdated?.has(booking?.id);
              
              return (
                <div 
                  key={booking?.id} 
                  className={`bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-all duration-300 ${
                    isRecentlyUpdated ? 'ring-2 ring-green-200 bg-green-50/50' : ''
                  }`}
                >
                  {/* Event Image */}
                  <div className="aspect-video bg-muted relative">
                    {booking?.events?.image_url ? (
                      <img
                        src={booking?.events?.image_url}
                        alt={booking?.events?.title || 'Event'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Image" size={32} className="text-muted-foreground" />
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking?.booking_status)}`}>
                      {booking?.booking_status?.charAt(0)?.toUpperCase() + booking?.booking_status?.slice(1)}
                    </div>
                    
                    {/* Time indicator */}
                    {eventDate && (
                      <div className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                        isUpcoming 
                          ? 'bg-green-600 text-white' :'bg-gray-600 text-white'
                      }`}>
                        {getTimeUntilEvent(booking?.events?.event_date)}
                      </div>
                    )}
                  </div>

                  {/* Event Details */}
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-foreground line-clamp-1">
                        {booking?.events?.title || 'Untitled Event'}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {booking?.events?.venue_name || 'Venue TBD'}
                      </p>
                    </div>

                    {/* Date and Time */}
                    {eventDate && (
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Icon name="Calendar" size={14} className="mr-2" />
                        <span>
                          {eventDate?.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        <Icon name="Clock" size={14} className="ml-3 mr-2" />
                        <span>
                          {eventDate?.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}

                    {/* Ticket Info */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-muted-foreground">
                        <Icon name="Ticket" size={14} className="mr-2" />
                        <span>
                          {booking?.quantity || 1} × {booking?.event_tickets?.ticket_name || 'General'}
                        </span>
                      </div>
                      <div className="font-semibold text-foreground">
                        ${booking?.total_amount?.toFixed(2) || '0.00'}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleViewTicket(booking)}
                      >
                        <Icon name="QrCode" size={14} className="mr-1" />
                        View Ticket
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSendSMS(booking)}
                      >
                        <Icon name="MessageSquare" size={14} className="mr-1" />
                        Send SMS
                      </Button>
                    </div>

                    {/* Secondary Actions */}
                    <div className="pt-2">
                      <Button variant="ghost" size="sm" className="w-full" asChild>
                        <Link to={`/events/${booking?.event_id}`}>
                          <Icon name="Eye" size={14} className="mr-2" />
                          View Event Details
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Ticket Modal */}
      <TicketModal 
        booking={selectedBooking} 
        isOpen={showTicketModal} 
        onClose={() => {
          setShowTicketModal(false);
          setSelectedBooking(null);
        }} 
      />
      {/* SMS Send Modal */}
      <SMSSendModal 
        isOpen={showSMSModal}
        onClose={() => {
          setShowSMSModal(false);
          setSmsBooking(null);
        }}
        booking={smsBooking}
        onSuccess={handleSMSSuccess}
        onError={handleSMSError}
      />
      {/* Real-time Test Component */}
      <RealtimeTest />
    </div>
  );
};

export default MyTickets;